import { groupBy } from "lodash";
import { sortDateDescending } from "@/core/utils/date";
import {
  GroupedPeriodSpiffs,
  EnrichedGroupedPeriodSpiffs,
} from "@/spiffs/spiffs.d";
import { calculateSpiffCounts } from "@/spiffs/utils/calculate-spiff-counts";
import { DetailedUser, EnrichedUserWithSpiffs } from "@/user/user";

/**
 * Enrich a grouped payroll period with user data
 * Groups spiffs by user and calculates counts per user
 * Also calculates whether this period can be locked (only if it's the oldest unlockable past period)
 */
export const enrichPayrollPeriod = (
  groupedPeriod: GroupedPeriodSpiffs,
  businessUsers: DetailedUser[],
  allPeriods: GroupedPeriodSpiffs[],
): EnrichedGroupedPeriodSpiffs => {
  // Create a lookup map for business users
  const userLookup = businessUsers.reduce(
    (lookup, user) => {
      lookup[user.id] = user;
      return lookup;
    },
    {} as Record<string, DetailedUser>,
  );

  // Group spiffs by user_id
  const spiffsByUser = groupBy(groupedPeriod.spiffs, "user_id");

  // Create enriched user objects
  const enrichedUsers: EnrichedUserWithSpiffs[] = Object.entries(
    spiffsByUser,
  ).map(([userId, userSpiffs]) => {
    const user = userLookup[userId];

    // Sort spiffs by event_date (descending - newest first) within each user
    const sortedUserSpiffs = [...userSpiffs].sort((a, b) =>
      sortDateDescending(a.event_date, b.event_date),
    );

    // Calculate counts only for pending (non-dismissed) spiffs
    // Components expect counts to exclude dismissed spiffs for display/export
    const pendingSpiffs = sortedUserSpiffs.filter((s) => !s.dismissed_at);
    const counts = calculateSpiffCounts(pendingSpiffs);

    // If user exists in lookup, merge with counts
    if (user) {
      return {
        ...user,
        spiffs: sortedUserSpiffs,
        ...counts,
      } as EnrichedUserWithSpiffs;
    }

    // Fallback for missing user - create minimal DetailedUser structure
    return {
      id: userId,
      first_name: null,
      last_name: null,
      role: "User" as const,
      email: "",
      crm_external_id: "",
      parentable_id: "",
      parentable_type: "Business" as const,
      created_at: "",
      updated_at: "",
      jti: "",
      qr_code_url: "",
      is_notifications_enabled: false,
      discarded_at: null,
      spiff_review_requested_at: null,
      spiffs: sortedUserSpiffs,
      ...counts,
    } as EnrichedUserWithSpiffs;
  });

  // Calculate if this period can be locked
  // Periods are ordered newest to oldest (index 0 = newest, last index = oldest)
  // Find the oldest period (last in array) that has non-dismissed spiffs, is past, and is NOT locked
  const oldestUnlockedPeriodWithPendingSpiffs = allPeriods
    .slice()
    .reverse()
    .find(
      (p) =>
        p.period.status === "past" &&
        !p.isLocked &&
        p.spiffs.filter((s) => !s.dismissed_at).length > 0,
    );

  // A period can be locked if:
  // 1. It's the oldest period with pending spiffs (last in array = oldest chronologically)
  // 2. It's not already locked
  // 3. It's a past period
  const canLock =
    oldestUnlockedPeriodWithPendingSpiffs?.period.id ===
      groupedPeriod.period.id &&
    !groupedPeriod.isLocked &&
    groupedPeriod.period.status === "past";

  return {
    ...groupedPeriod,
    enrichedUsers,
    canLock,
  };
};

/**
 * Enrich multiple grouped payroll periods with user data
 */
export const enrichPayrollPeriods = (
  groupedPeriods: GroupedPeriodSpiffs[],
  businessUsers: DetailedUser[],
): EnrichedGroupedPeriodSpiffs[] =>
  groupedPeriods.map((groupedPeriod) =>
    enrichPayrollPeriod(groupedPeriod, businessUsers, groupedPeriods),
  );
